import dayjs from 'dayjs';
import Image from 'next/image';
import { useTheme } from 'styled-components';

import Tooltip from '~components/common/Tooltip';
import ExclamationMark from '~components/Icon/ExclamationMark';
import Text from '~components/Text';
import useMedia from '~hooks/useMedia';
import { COMPANY_SOURCE, CompanyStates, SummaryEmployee } from '~types/company';
import { getSalaryRate } from '~utils/company/company';
import { parseToPercent } from '~utils/formatted/number';
import { isEmpty } from '~utils/lodash/isEmpty';

import { S } from './style';

interface Props {
  employee: SummaryEmployee;
  companyStates: CompanyStates;
}
function EmployeeCard({ employee, companyStates }: Props) {
  const { isMobile } = useMedia();
  const theme = useTheme();
  function employeeDisplay(employeeData: number, type: 'percent' | 'number') {
    if (type === 'percent') {
      return parseToPercent(employeeData, 0);
    }

    return isEmpty(employeeData) ? '정보없음' : `${employeeData.toLocaleString()}명`;
  }

  const rate = getSalaryRate(employee.rate ?? 0);

  return (
    <S.SummaryCardItem>
      <S.Label>
        <img src={`/images/${isMobile ? 'employee_m.png' : 'employee.png'}`} alt='money' />
        <S.Title typography='midium150_C1'>총 인원</S.Title>
      </S.Label>
      {companyStates.employee ? (
        <S.Content>
          <S.TitleWrapper isEmpty={isEmpty(employee.total)}>
            <Text typography={isMobile ? 'bold150_T1' : 'bold150_B1'}>
              {employeeDisplay(employee.total, 'number')}
            </Text>
            {rate && rate <= 50 && !isEmpty(employee.total) && <S.Badge>상위 {rate}%</S.Badge>}
          </S.TitleWrapper>
          <S.SubTitleWrapper>
            <S.SubTitle isEmpty={isEmpty(employee.left)}>
              <Text typography='midium120_C1'>퇴사</Text>
              <Text typography='bold120_C1'>{employeeDisplay(employee.left, 'number')}</Text>
              {!isEmpty(employee.leftRate) && (
                <Text typography='bold120_C1'>
                  ({employeeDisplay(employee.leftRate, 'percent')}%)
                </Text>
              )}
            </S.SubTitle>
            <S.SubTitle isEmpty={isEmpty(employee.hired)}>
              <Text typography='midium120_C1'>입사</Text>
              <Text typography='bold120_C1'>{employeeDisplay(employee.hired, 'number')}</Text>
              {!isEmpty(employee.hiredRate) && (
                <Text typography='bold120_C1'>
                  ({employeeDisplay(employee.hiredRate, 'percent')}%)
                </Text>
              )}
            </S.SubTitle>
          </S.SubTitleWrapper>
          <S.Source typography='midium150_C1'>
            출처 :{' '}
            {employee.source
              ? `${COMPANY_SOURCE[employee.source]} ${dayjs(employee.updatedAt).format('YYYY.MM')} `
              : '정보없음'}
            {employee.updatedAt && (
              <Tooltip
                tooltipText={'퇴사율과 인원증가율은 최근 1년을 기준으로 합니다.'}
                position={isMobile ? 'right' : 'center'}>
                <ExclamationMark width={15} color={theme.colors.gray700} />
              </Tooltip>
            )}
          </S.Source>
        </S.Content>
      ) : (
        <S.Content>
          <Text typography={isMobile ? 'bold150_T1' : 'bold150_B1'}>비공개</Text>
        </S.Content>
      )}
    </S.SummaryCardItem>
  );
}

export default EmployeeCard;
